import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Tab, Tabs, TextField, Typography, Divider, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Snackbar, Alert } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ScheduleIcon from '@mui/icons-material/Schedule'
import ListAltIcon from '@mui/icons-material/ListAlt'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { SelectChangeEvent } from '@mui/material/Select'
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/he'
import 'dayjs/locale/es'
import 'dayjs/locale/ar'
import 'dayjs/locale/th'
import { getTeams, getYards } from '../services/api'
import { getUsers } from '../services/users'
import { createTask, getTasks, updateTask, deleteTask } from '../services/tasks'
import { getInspections } from '../services/inspections'
import { getTreatments } from '../services/treatments'
import { getHarvests } from '../services/harvests'
import { getMovements } from '../services/movements'
import { Task, User } from '../types'
import { useSocket } from '../contexts/SocketContext'
import TaskKanbanBoard from '../components/tasks/TaskKanbanBoard'
import TaskGroupedTable from '../components/tasks/TaskGroupedTable'
import { getTaskTypeLabel, formatTaskDate } from '../components/tasks/taskTypeLabel'

const PageContainer = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.lg};
`

const Panels = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 1.1fr) minmax(320px, 1fr);
  gap: ${props => props.theme.spacing.xxl};
  align-items: start;
`

const Panel = styled(Paper)`
  padding: ${props => props.theme.spacing.xxl};
  border-radius: ${props => props.theme.borderRadius.xxlarge} !important;
  box-shadow: ${props => props.theme.shadows.lg} !important;
  background: ${props => props.theme.colors.backgroundSecondary} !important;
`

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.lg};
  max-height: 520px;
  overflow-y: auto;
  padding-right: ${props => props.theme.spacing.sm};
`

const HourRow = styled.div<{ highlight?: boolean }>`
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.large};
  background: ${props =>
    props.highlight ? 'rgba(246, 180, 0, 0.12)' : 'transparent'};
`

const HourLabel = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textSecondary};
`

const HourTasks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`

const TaskRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.sm};
`

const TaskTitle = styled.span`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`

const TaskMeta = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
`

const UpcomingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  max-height: 520px;
  overflow-y: auto;
  padding-right: ${props => props.theme.spacing.sm};
`

const UpcomingCard = styled.div<{ overdue?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.large};
  background: ${props => props.overdue ? 'rgba(211, 47, 47, 0.06)' : 'rgba(246, 180, 0, 0.06)'};
  border-left: 3px solid ${props => props.overdue ? '#d32f2f' : '#F6B400'};
  transition: background 0.15s;
  &:hover { background: ${props => props.overdue ? 'rgba(211, 47, 47, 0.1)' : 'rgba(246, 180, 0, 0.12)'}; }
`

function TaskManagementScreen(): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const lang = (i18n?.language || 'en').split('-')[0]
  const { on, off } = useSocket()
  const [tasks, setTasks] = useState<Task[]>([])
  const [teams, setTeams] = useState<any[]>([])
  const [yards, setYards] = useState<any[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [inspections, setInspections] = useState<any[]>([])
  const [treatments, setTreatments] = useState<any[]>([])
  const [harvests, setHarvests] = useState<any[]>([])
  const [movements, setMovements] = useState<any[]>([])
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [taskType, setTaskType] = useState('Inspect')
  const [customTaskType, setCustomTaskType] = useState('')
  const [dueDateTime, setDueDateTime] = useState(dayjs().format('YYYY-MM-DDTHH:mm'))
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const timelineRef = useRef<HTMLDivElement | null>(null)
  const [assigneeType, setAssigneeType] = useState<'team' | 'user'>('team')
  const [assigneeId, setAssigneeId] = useState('')
  const [yardId, setYardId] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Top-level page tabs: calendar, upcoming, board
  const [pageTab, setPageTab] = useState<'calendar' | 'upcoming' | 'board'>('calendar')
  const [calendarSubTab, setCalendarSubTab] = useState<'tasks' | 'daily'>('tasks')
  const [boardSubTab, setBoardSubTab] = useState<'kanban' | 'table'>('kanban')

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editDueDate, setEditDueDate] = useState('')

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingTask, setDeletingTask] = useState<Task | null>(null)

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const fetchTaskData = useCallback(async (): Promise<[Task[], any[], any[], User[], any[], any[], any[], any[]]> => {
    const [tasksRes, teamsRes, yardsRes, usersRes, inspectionsRes, treatmentsRes, harvestsRes, movementsRes] = await Promise.all([
      getTasks(),
      getTeams(),
      getYards(),
      getUsers(),
      getInspections(),
      getTreatments(),
      getHarvests(),
      getMovements(),
    ])
    return [
      tasksRes.data || [],
      teamsRes.data || [],
      yardsRes.data || [],
      usersRes.data || [],
      inspectionsRes.data || [],
      treatmentsRes.data || [],
      harvestsRes.data || [],
      movementsRes.data || [],
    ]
  }, [])

  const applyTaskData = useCallback((data: [Task[], any[], any[], User[], any[], any[], any[], any[]]): void => {
    const [tasksData, teamsData, yardsData, usersData, inspectionsData, treatmentsData, harvestsData, movementsData] = data
    setTasks(tasksData)
    setTeams(teamsData)
    setYards(yardsData)
    setUsers(usersData)
    setInspections(inspectionsData)
    setTreatments(treatmentsData)
    setHarvests(harvestsData)
    setMovements(movementsData)
  }, [])

  const loadTaskData = useCallback(async (): Promise<void> => {
    try {
      applyTaskData(await fetchTaskData())
    } catch (error) {
      console.error('Error loading task data:', error)
    }
  }, [applyTaskData, fetchTaskData])

  useEffect(() => {
    loadTaskData()
  }, [loadTaskData])

  useEffect(() => {
    const handleTaskUpdated = () => { loadTaskData() }
    on('task:updated', handleTaskUpdated)
    return () => { off('task:updated', handleTaskUpdated) }
  }, [on, off, loadTaskData])

  function isFormValid(): boolean {
    const resolvedType = taskType === 'Custom' ? customTaskType : taskType
    return !!assigneeId && !!resolvedType && !!yardId
  }

  const tasksForDate = useMemo(() => {
    return tasks.filter(task => dayjs(task.dueDate).isSame(selectedDate, 'day'))
  }, [tasks, selectedDate])

  const isTaskDoneByAction = useCallback(
    (task: Task): boolean => {
      const taskDay = dayjs(task.dueDate)
      const matchesYard = (actionYardId?: string | number): boolean => {
        if (!task.yardId) return true
        return String(actionYardId) === String(task.yardId)
      }
      if (task.type === 'Inspect') {
        return inspections.some((inspection: any) =>
          dayjs(inspection.inspectionDate).isSame(taskDay, 'day') && matchesYard(inspection.yardId)
        )
      }
      if (task.type === 'Treatment') {
        return treatments.some((treatment: any) =>
          dayjs(treatment.treatmentDate).isSame(taskDay, 'day') && matchesYard(treatment.yardId)
        )
      }
      if (task.type === 'Harvest') {
        return harvests.some((harvest: any) =>
          dayjs(harvest.harvestDate).isSame(taskDay, 'day') && matchesYard(harvest.yardId)
        )
      }
      if (task.type === 'Move') {
        return movements.some((movement: any) =>
          dayjs(movement.movementDate || movement.createdAt).isSame(taskDay, 'day') &&
          (!task.yardId || matchesYard(movement.fromYardId) || matchesYard(movement.toYardId))
        )
      }
      return task.status === 'done'
    },
    [harvests, inspections, movements, treatments]
  )

  useEffect(() => {
    const isToday = selectedDate.isSame(dayjs(), 'day')
    if (!isToday || !timelineRef.current) return
    const target = timelineRef.current.querySelector('[data-current-hour="true"]') as HTMLElement | null
    if (!target) return
    target.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [selectedDate, tasksForDate])

  const activitiesForDate = useMemo(() => {
    const activities: Array<{
      id: string; title: string; type: string; time: string;
      yardId?: string | number; userId?: string | number; isDone?: boolean
    }> = []
    const selectedDay = selectedDate.format('YYYY-MM-DD')
    tasksForDate.forEach(task => {
      activities.push({ id: `task-${task.id}`, title: task.title, type: t('tasks.activity.task'), time: task.dueDate,
        yardId: task.yardId, userId: task.userId || task.createdBy, isDone: isTaskDoneByAction(task) })
    })
    inspections.forEach((inspection: any) => {
      if (dayjs(inspection.inspectionDate).format('YYYY-MM-DD') === selectedDay) {
        activities.push({ id: `inspection-${inspection.id}`, title: t('tasks.activity.inspection'), type: t('tasks.activity.inspection'),
          time: inspection.inspectionDate, yardId: inspection.yardId, userId: inspection.userId, isDone: true })
      }
    })
    treatments.forEach((treatment: any) => {
      if (dayjs(treatment.treatmentDate).format('YYYY-MM-DD') === selectedDay) {
        activities.push({ id: `treatment-${treatment.id}`, title: treatment.treatmentType || t('tasks.activity.treatment'), type: t('tasks.activity.treatment'),
          time: treatment.treatmentDate, yardId: treatment.yardId, userId: treatment.userId, isDone: true })
      }
    })
    harvests.forEach((harvest: any) => {
      if (dayjs(harvest.harvestDate).format('YYYY-MM-DD') === selectedDay) {
        activities.push({ id: `harvest-${harvest.id}`, title: t('tasks.activity.harvestTitle', { honeyType: harvest.honeyType || t('inventory.honey') }), type: t('tasks.activity.harvest'),
          time: harvest.harvestDate, yardId: harvest.yardId, userId: harvest.userId, isDone: true })
      }
    })
    movements.forEach((movement: any) => {
      const movementDate = movement.movementDate || movement.createdAt
      if (dayjs(movementDate).format('YYYY-MM-DD') === selectedDay) {
        activities.push({
          id: `movement-${movement.id}`,
          title: t('tasks.activity.movementTitle', {
            quantity: movement.quantity,
            boxType: movement.boxType ? t(`inventory.boxTypes.${movement.boxType}`, { defaultValue: movement.boxType }) : '',
          }),
          type: t('tasks.activity.movement'),
          time: movementDate, yardId: movement.fromYardId, userId: movement.userId, isDone: true })
      }
    })
    return activities
  }, [selectedDate, tasksForDate, inspections, treatments, harvests, movements, isTaskDoneByAction, t])

  const activitiesByHour = useMemo(() => {
    const map = new Map<number, typeof activitiesForDate>()
    activitiesForDate.forEach(activity => {
      const hour = dayjs(activity.time).hour()
      const list = map.get(hour) || []
      list.push(activity)
      map.set(hour, list)
    })
    return map
  }, [activitiesForDate])

  const userNameById = useMemo(() => {
    const map = new Map<string, string>()
    users.forEach(item => { map.set(String(item.id), item.name || item.email) })
    return map
  }, [users])

  const yardNameById = useMemo(() => {
    const map = new Map<string, string>()
    yards.forEach(item => { map.set(String(item.id || item._id), item.name) })
    return map
  }, [yards])

  const teamNameById = useMemo(() => {
    const map = new Map<string, string>()
    teams.forEach((item: any) => { map.set(String(item.id || item._id), item.name) })
    return map
  }, [teams])

  const getAssigneeLabel = useCallback((task: Task): string => {
    if (task.userId) return userNameById.get(String(task.userId)) || t('tasks.assignee.user')
    if (task.teamId) return teamNameById.get(String(task.teamId)) || t('tasks.assignee.team')
    return '-'
  }, [teamNameById, userNameById, t])

  const getCreatorLabel = useCallback((task: Task): string => {
    return task.createdBy ? userNameById.get(String(task.createdBy)) || t('tasks.assignee.user') : '-'
  }, [userNameById, t])

  const getYardLabel = useCallback((task: Task): string => {
    if (!task.yardId) return '-'
    return yardNameById.get(String(task.yardId)) || t('yard.title')
  }, [yardNameById, t])

  const hoursWindow = useMemo(() => {
    const isToday = selectedDate.isSame(dayjs(), 'day')
    const anchorHour = isToday ? dayjs().hour() : 12
    return Array.from({ length: 24 }, (_, index) => (anchorHour - 12 + index + 24) % 24)
  }, [selectedDate])

  const upcomingTasks = useMemo(() => {
    return [...tasks]
      .filter(task => task.status !== 'done' && !isTaskDoneByAction(task))
      .sort((a, b) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf())
  }, [tasks, isTaskDoneByAction])

  // Option values are the stored API task types; only their displayed label is translated
  const taskTypeOptions = useMemo(
    () => ['Inspect', 'Treatment', 'Move', 'Harvest', 'Feed Sugar Syrup', 'Feed Pollen Substitute', 'Requeen Hive', 'Replace Old Frames', 'Clean Hive Equipment', 'Weigh Hives', 'Custom'],
    []
  )

  function getTaskPayload(): Partial<Task> {
    const resolvedType = taskType === 'Custom' ? customTaskType : taskType
    const dueDate = dayjs(dueDateTime)
    return {
      title: title || getTaskTypeLabel(t, resolvedType),
      description,
      dueDate: dueDate.toISOString(),
      status: 'open',
      type: resolvedType,
      yardId,
      teamId: assigneeType === 'team' ? assigneeId : undefined,
      userId: assigneeType === 'user' ? assigneeId : undefined,
    }
  }

  async function handleSubmit(): Promise<void> {
    if (!isFormValid()) return
    try {
      setIsSubmitting(true)
      await createTask(getTaskPayload())
      setTitle(''); setDescription(''); setAssigneeId(''); setYardId(''); setCustomTaskType('')
      setCreateDialogOpen(false)
      await loadTaskData()
    } catch (error) {
      console.error('Error creating task:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleEditOpen(task: Task): void {
    setEditingTask(task); setEditTitle(task.title); setEditDescription(task.description || '')
    setEditDueDate(dayjs(task.dueDate).format('YYYY-MM-DDTHH:mm')); setEditDialogOpen(true)
  }

  async function handleEditSave(): Promise<void> {
    if (!editingTask) return
    try {
      setIsSubmitting(true)
      await updateTask(editingTask.id, { title: editTitle, description: editDescription, dueDate: dayjs(editDueDate).toISOString() })
      setEditDialogOpen(false)
      setSnackbar({ open: true, message: t('tasks.editSuccess'), severity: 'success' })
      await loadTaskData()
    } catch (error) {
      console.error('Error updating task:', error)
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleDeleteOpen(task: Task): void {
    setDeletingTask(task); setDeleteDialogOpen(true)
  }

  async function handleDeleteConfirm(): Promise<void> {
    if (!deletingTask) return
    try {
      setIsSubmitting(true)
      await deleteTask(deletingTask.id)
      setDeleteDialogOpen(false)
      setSnackbar({ open: true, message: t('tasks.deleteSuccess'), severity: 'success' })
      await loadTaskData()
    } catch (error) {
      console.error('Error deleting task:', error)
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleMarkDone(task: Task): Promise<void> {
    try {
      await updateTask(task.id, { status: 'done' })
      setSnackbar({ open: true, message: t('tasks.markDoneSuccess'), severity: 'success' })
      await loadTaskData()
    } catch (error) {
      console.error('Error marking task done:', error)
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' })
    }
  }

  return (
    <PageContainer>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4" fontWeight={700}>{t('tasks.title')}</Typography>
        <Button variant="contained" size="small" onClick={() => setCreateDialogOpen(true)}>
          {t('tasks.createButton')}
        </Button>
      </Box>

      {/* Top-level page tabs */}
      <Tabs
        value={pageTab}
        onChange={(_, v) => setPageTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="calendar"
          icon={<CalendarMonthIcon fontSize="small" />}
          iconPosition="start"
          label={t('tasks.calendarTitle')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        />
        <Tab
          value="upcoming"
          icon={<ListAltIcon fontSize="small" />}
          iconPosition="start"
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              {t('tasks.upcoming')}
              {upcomingTasks.length > 0 && (
                <Chip label={upcomingTasks.length} size="small" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Box>
          }
          sx={{ textTransform: 'none', fontWeight: 600 }}
        />
        <Tab
          value="board"
          icon={<ViewKanbanIcon fontSize="small" />}
          iconPosition="start"
          label={t('tasks.board')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        />
      </Tabs>

      {/* Calendar Tab - two-panel layout */}
      {pageTab === 'calendar' && (
        <Panels>
          <Panel elevation={0}>
            <Typography variant="h6" fontWeight={700} gutterBottom>{t('tasks.calendarTitle')}</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={lang}>
              <DateCalendar value={selectedDate} onChange={(date: Dayjs | null) => date && setSelectedDate(date)} />
            </LocalizationProvider>
            <Box mt={3}>
              <Typography variant="subtitle1" fontWeight={700}>{t('tasks.tasksForDate', { date: formatTaskDate(selectedDate.toDate(), lang, { month: 'short', day: 'numeric', year: 'numeric' }) })}</Typography>
              {tasksForDate.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('tasks.noTasks')}</Typography>
              ) : (
                tasksForDate.map(task => (
                  <Box key={task.id} mt={1} p={2} borderRadius={2} bgcolor="rgba(246, 180, 0, 0.12)">
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>{task.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{task.description || t('tasks.noDescription')}</Typography>
                        <Typography variant="caption" color="text.secondary">{t('tasks.creatorToAssignee', { creator: getCreatorLabel(task), assignee: getAssigneeLabel(task) })}</Typography>
                      </Box>
                      <Box display="flex" gap={0.5}>
                        {task.status !== 'done' && !isTaskDoneByAction(task) && (
                          <Tooltip title={t('tasks.markDone')}>
                            <IconButton size="small" color="success" onClick={() => handleMarkDone(task)}>
                              <CheckCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('tasks.edit')}>
                          <IconButton size="small" color="primary" onClick={() => handleEditOpen(task)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('tasks.delete')}>
                          <IconButton size="small" color="error" onClick={() => handleDeleteOpen(task)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Panel>
          <Panel elevation={0}>
            <Tabs value={calendarSubTab} onChange={(_, v) => setCalendarSubTab(v)} sx={{ minHeight: 36, mb: 1 }}>
              <Tab value="tasks" label={t('tasks.tasksForDate', { date: formatTaskDate(selectedDate.toDate(), lang, { month: 'short', day: 'numeric' }) })} sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600 }} />
              <Tab value="daily" icon={<ScheduleIcon fontSize="small" />} iconPosition="start" label={t('tasks.daily')} sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600 }} />
            </Tabs>

            {calendarSubTab === 'daily' && (
              <Timeline ref={timelineRef}>
                {hoursWindow.map((hour, index) => {
                  const hourActivities = activitiesByHour.get(hour) || []
                  const label = dayjs().hour(hour).minute(0).format('HH:00')
                  const isToday = selectedDate.isSame(dayjs(), 'day')
                  const isCurrentHour = isToday && hour === dayjs().hour()
                  return (
                    <HourRow key={hour} highlight={isToday && index === 12} data-current-hour={isCurrentHour ? 'true' : 'false'}>
                      <HourLabel>{label}</HourLabel>
                      <HourTasks>
                        {hourActivities.length === 0 ? (
                          <TaskMeta>&nbsp;</TaskMeta>
                        ) : (
                          hourActivities.map(activity => (
                            <TaskRow key={activity.id}>
                              <Box display="flex" flexDirection="column">
                                <TaskTitle>{activity.title}</TaskTitle>
                                <TaskMeta>
                                  {activity.type}
                                  {activity.yardId && yardNameById.get(String(activity.yardId)) && <> &bull; {yardNameById.get(String(activity.yardId))}</>}
                                  {activity.userId && userNameById.get(String(activity.userId)) && <> &bull; {userNameById.get(String(activity.userId))}</>}
                                </TaskMeta>
                              </Box>
                              {activity.isDone ? <CheckCircleOutlineIcon fontSize="small" color="success" /> : <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                            </TaskRow>
                          ))
                        )}
                      </HourTasks>
                    </HourRow>
                  )
                })}
              </Timeline>
            )}

            {calendarSubTab === 'tasks' && (
              <UpcomingList>
                {tasksForDate.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography color="text.secondary">{t('tasks.noTasks')}</Typography>
                  </Box>
                ) : (
                  tasksForDate.map(task => {
                    const isDone = isTaskDoneByAction(task) || task.status === 'done'
                    return (
                      <UpcomingCard key={task.id}>
                        {isDone ? <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ mt: 0.3 }} /> : <RadioButtonUncheckedIcon fontSize="small" sx={{ mt: 0.3, color: '#bbb' }} />}
                        <Box flex={1}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="subtitle2" fontWeight={600}>{task.title}</Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Chip label={getTaskTypeLabel(t, task.type)} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                              {!isDone && (
                                <Tooltip title={t('tasks.markDone')}>
                                  <IconButton size="small" color="success" onClick={() => handleMarkDone(task)}>
                                    <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title={t('tasks.edit')}>
                                <IconButton size="small" color="primary" onClick={() => handleEditOpen(task)}>
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('tasks.delete')}>
                                <IconButton size="small" color="error" onClick={() => handleDeleteOpen(task)}>
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          {task.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>{task.description}</Typography>}
                          <Box display="flex" gap={2} mt={0.5}>
                            <Typography variant="caption" color="text.secondary">{dayjs(task.dueDate).format('HH:mm')}</Typography>
                            {task.yardId && yardNameById.get(String(task.yardId)) && <Typography variant="caption" color="text.secondary">{yardNameById.get(String(task.yardId))}</Typography>}
                            <Typography variant="caption" color="text.secondary">{getAssigneeLabel(task)}</Typography>
                          </Box>
                        </Box>
                      </UpcomingCard>
                    )
                  })
                )}
              </UpcomingList>
            )}
          </Panel>
        </Panels>
      )}

      {/* Upcoming Tasks Tab - full page */}
      {pageTab === 'upcoming' && (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <UpcomingList style={{ maxHeight: 'none' }}>
            {upcomingTasks.length === 0 ? (
              <Box textAlign="center" py={4}>
                <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                <Typography color="text.secondary">{t('tasks.noUpcoming')}</Typography>
              </Box>
            ) : (
              upcomingTasks.map(task => {
                const due = dayjs(task.dueDate)
                const isOverdue = due.isBefore(dayjs(), 'day')
                const isToday = due.isSame(dayjs(), 'day')
                const isTomorrow = due.isSame(dayjs().add(1, 'day'), 'day')
                const dueTime = due.format('HH:mm')
                let dateLabel = t('tasks.dueAt', {
                  date: formatTaskDate(due.toDate(), lang, { month: 'short', day: 'numeric' }),
                  time: dueTime,
                })
                if (isOverdue) dateLabel = t('tasks.overdueSince', { date: formatTaskDate(due.toDate(), lang, { month: 'short', day: 'numeric' }) })
                else if (isToday) dateLabel = t('tasks.dueTodayAt', { time: dueTime })
                else if (isTomorrow) dateLabel = t('tasks.dueTomorrowAt', { time: dueTime })

                return (
                  <UpcomingCard key={task.id} overdue={isOverdue}>
                    <RadioButtonUncheckedIcon fontSize="small" sx={{ mt: 0.3, color: isOverdue ? '#d32f2f' : '#bbb' }} />
                    <Box flex={1}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="subtitle2" fontWeight={600}>{task.title}</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Chip label={getTaskTypeLabel(t, task.type)} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                          <Tooltip title={t('tasks.markDone')}>
                            <IconButton size="small" color="success" onClick={() => handleMarkDone(task)}>
                              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('tasks.edit')}>
                            <IconButton size="small" color="primary" onClick={() => handleEditOpen(task)}>
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('tasks.delete')}>
                            <IconButton size="small" color="error" onClick={() => handleDeleteOpen(task)}>
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      {task.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>{task.description}</Typography>}
                      <Box display="flex" gap={2} mt={0.5}>
                        <Typography variant="caption" color={isOverdue ? 'error.main' : 'text.secondary'} fontWeight={isOverdue ? 600 : 400}>{dateLabel}</Typography>
                        {task.yardId && yardNameById.get(String(task.yardId)) && <Typography variant="caption" color="text.secondary">{yardNameById.get(String(task.yardId))}</Typography>}
                        <Typography variant="caption" color="text.secondary">{getAssigneeLabel(task)}</Typography>
                      </Box>
                    </Box>
                  </UpcomingCard>
                )
              })
            )}
          </UpcomingList>
        </Paper>
      )}

      {/* Board Tab - full page */}
      {pageTab === 'board' && (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Tabs value={boardSubTab} onChange={(_, v) => setBoardSubTab(v)} sx={{ minHeight: 32, mb: 2 }}>
            <Tab value="kanban" label={t('tasks.kanban')} sx={{ minHeight: 32, textTransform: 'none', fontSize: 13 }} />
            <Tab value="table" label={t('tasks.groupedTable')} sx={{ minHeight: 32, textTransform: 'none', fontSize: 13 }} />
          </Tabs>
          {boardSubTab === 'kanban' ? (
            <TaskKanbanBoard
              tasks={tasks}
              getAssigneeLabel={getAssigneeLabel}
              getYardLabel={getYardLabel}
              onEdit={handleEditOpen}
              onDelete={handleDeleteOpen}
              onMarkDone={handleMarkDone}
            />
          ) : (
            <TaskGroupedTable
              tasks={tasks}
              getAssigneeLabel={getAssigneeLabel}
              getYardLabel={getYardLabel}
              onEdit={handleEditOpen}
              onDelete={handleDeleteOpen}
              onMarkDone={handleMarkDone}
            />
          )}
        </Paper>
      )}

      {/* Create Task Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('tasks.createTitle')}</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>{t('tasks.fields.type')}</InputLabel>
              <Select
                value={taskType}
                label={t('tasks.fields.type')}
                onChange={(event: SelectChangeEvent) => {
                  const value = event.target.value as string
                  setTaskType(value)
                  if (!title || title === taskType || title === getTaskTypeLabel(t, taskType)) {
                    setTitle(value === 'Custom' ? '' : getTaskTypeLabel(t, value))
                  }
                }}
              >
                {taskTypeOptions.map(option => <MenuItem key={option} value={option}>{getTaskTypeLabel(t, option)}</MenuItem>)}
              </Select>
            </FormControl>
            {taskType === 'Custom' && (
              <TextField label={t('tasks.fields.customType')} value={customTaskType}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCustomTaskType(event.target.value)} fullWidth />
            )}
            <TextField label={t('tasks.fields.title')} value={title}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTitle(event.target.value)} fullWidth />
            <TextField label={t('tasks.fields.description')} value={description}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDescription(event.target.value)} fullWidth multiline minRows={3} />
            <TextField label={t('tasks.fields.dueDateTime')} type="datetime-local" value={dueDateTime}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDueDateTime(event.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            <FormControl fullWidth>
              <InputLabel>{t('tasks.fields.yard')}</InputLabel>
              <Select value={yardId} label={t('tasks.fields.yard')}
                onChange={(event: SelectChangeEvent) => setYardId(event.target.value as string)}>
                {yards.map((item: any) => <MenuItem key={item.id || item._id} value={item.id || item._id}>{item.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('tasks.fields.assigneeType')}</InputLabel>
              <Select value={assigneeType} label={t('tasks.fields.assigneeType')}
                onChange={(event: SelectChangeEvent) => setAssigneeType(event.target.value as 'team' | 'user')}>
                <MenuItem value="team">{t('tasks.assignee.team')}</MenuItem>
                <MenuItem value="user">{t('tasks.assignee.user')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('tasks.fields.assignee')}</InputLabel>
              <Select value={assigneeId} label={t('tasks.fields.assignee')}
                onChange={(event: SelectChangeEvent) => setAssigneeId(event.target.value as string)}>
                {(assigneeType === 'team' ? teams : users).map(item => <MenuItem key={item.id} value={item.id}>{item.name || item.email}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('button.cancel.text')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!isFormValid() || isSubmitting}>{t('tasks.createButton')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('tasks.edit')}</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={1}>
            <TextField label={t('tasks.fields.title')} value={editTitle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditTitle(e.target.value)} fullWidth />
            <TextField label={t('tasks.fields.description')} value={editDescription}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditDescription(e.target.value)} fullWidth multiline minRows={3} />
            <TextField label={t('tasks.fields.dueDate')} type="datetime-local" value={editDueDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditDueDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={isSubmitting}>{t('button.cancel.text')}</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={!editTitle || isSubmitting}>
            {isSubmitting ? t('button.save.loading') : t('button.save.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('tasks.delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('tasks.confirmDelete', { title: deletingTask?.title })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isSubmitting}>{t('button.cancel.text')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('button.delete.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </PageContainer>
  )
}

export default TaskManagementScreen
